import api from './api';
import { handleError } from '../utils/errorHandler';
import {
  CarteiraDiariaAtivos, CarteiraDiariaPatrimonioLiquido, CarteiraDiariaPassivo,
  CarteiraDiariaOutrosPassivos, CarteiraDiariaTesouraria,
  PosicaoAtualFundoResponse, MovimentacaoAtivo, MovimentacaoAtivoCreate,
  RentabilidadeAtivoResponse, CarteiraDiariaFundoResponse, RentabilidadeComparativaResponse,
  HistoricoAtivoResponse, HistoricoFundoResponse, MovimentacaoCotistaResponse,
} from '../types/carteiraDiaria';

export const getCarteiraAtivos = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = '',
  dataFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length
      ? `&ordering=${sortBy[0].desc ? '-' : ''}${sortBy[0].id}`
      : '';

    const searchParam = globalFilter ? `&search=${globalFilter}` : '';
    const dateParam = dataFilter ? `&data_posicao=${dataFilter}` : '';

    const queryString = `/ativos/carteira-diaria-ativos/?page=${pageIndex + 1}&page_size=${pageSize}`
      + `${sortParam}${searchParam}${dateParam}`;

    const response = await api.get<{ results: CarteiraDiariaAtivos[]; count: number }>(
      queryString
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar carteira diária de ativos');
    throw error;
  }
};

export const getCarteiraPatrimonioLiquido = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = '',
  dataFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length > 0 ? `&ordering=${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}` : '';
    const filterParam = globalFilter ? `&search=${globalFilter}` : '';
    const dateParam = dataFilter ? `&data_posicao=${dataFilter}` : '';

    const queryString = `/ativos/carteira-diaria-patrimonio-liquido/?page=${pageIndex + 1}&page_size=${pageSize}`
      + `${sortParam}${filterParam}${dateParam}`;

    const response = await api.get<{ results: CarteiraDiariaPatrimonioLiquido[], count: number }>(
      queryString
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar carteira diária de patrimônio líquido');
    throw error;
  }
};

export const getCarteiraPassivo = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = '',
  dataFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length > 0 ? `&ordering=${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}` : '';
    const filterParam = globalFilter ? `&search=${globalFilter}` : '';
    const dateParam = dataFilter ? `&data_posicao=${dataFilter}` : '';

    const queryString = `/ativos/carteira-diaria-passivo/?page=${pageIndex + 1}&page_size=${pageSize}`
      + `${sortParam}${filterParam}${dateParam}`;

    const response = await api.get<{ results: CarteiraDiariaPassivo[], count: number }>(
      queryString
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar carteira diária de passivo');
    throw error;
  }
};

export const getCarteiraOutrosPassivos = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = '',
  dataFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length > 0 ? `&ordering=${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}` : '';
    const filterParam = globalFilter ? `&search=${globalFilter}` : '';
    const dateParam = dataFilter ? `&data_posicao=${dataFilter}` : '';

    const queryString = `/ativos/carteira-diaria-outros-passivos/?page=${pageIndex + 1}&page_size=${pageSize}`
      + `${sortParam}${filterParam}${dateParam}`;

    const response = await api.get<{ results: CarteiraDiariaOutrosPassivos[], count: number }>(
      queryString
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar carteira diária de outros passivos');
    throw error;
  }
};

export const getCarteiraTesouraria = async (
  pageIndex: number,
  pageSize: number = 100,
  sortBy: any[] = [],
  globalFilter: string = '',
  dataFilter: string = ''
) => {
  try {
    const sortParam = sortBy.length > 0 ? `&ordering=${sortBy.map(sort => (sort.desc ? '-' : '') + sort.id).join(',')}` : '';
    const filterParam = globalFilter ? `&search=${globalFilter}` : '';
    const dateParam = dataFilter ? `&data_posicao=${dataFilter}` : '';

    const queryString = `/ativos/saldo-tesouraria/?page=${pageIndex + 1}&page_size=${pageSize}`
      + `${sortParam}${filterParam}${dateParam}`;

    const response = await api.get<{ results: CarteiraDiariaTesouraria[], count: number }>(
      queryString
    );

    return {
      results: response.data.results,
      totalPages: Math.ceil(response.data.count / pageSize),
    };
  } catch (error) {
    handleError(error, 'ao buscar saldo de tesouraria');
    throw error;
  }
};

export const updateCarteira = async (carteira: number, id: number, data: any) => {
  try {
    const response = await api.patch(`/ativos/${carteira}/${id}/`, data);
    return response.data;
  } catch (error) {
    handleError(error, 'ao atualizar carteira');
    throw error;
  }
};

export const getCarteiraPorFundoData = async (fundoId: string, dataPosicao: string) => {
  try {
    const response = await api.get(`/ativos/carteira-diaria/fundo/${fundoId}/data/${dataPosicao}/`);
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar dados da carteira por fundo e data');
    throw error;
  }
};

export const getPosicaoAtualFundo = async (fundoId: number, data?: string): Promise<PosicaoAtualFundoResponse> => {
  try {
    const dateParam = data ? `?data=${data}` : '';
    const response = await api.get<PosicaoAtualFundoResponse>(`/ativos/posicao-atual/${fundoId}/${dateParam}`);
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar posição atual do fundo');
    throw error;
  }
};

export const getMovimentacoesAtivo = async (
  fundoId: number,
  codigoAtivo: string,
  nomeAtivo?: string,
  adm?: string
): Promise<MovimentacaoAtivo[]> => {
  try {
    const nomeParam = nomeAtivo ? `&nome_ativo=${encodeURIComponent(nomeAtivo)}` : '';
    const admParam  = adm       ? `&adm=${encodeURIComponent(adm)}`             : '';
    const response = await api.get<MovimentacaoAtivo[]>(
      `/ativos/movimentacao-ativo/?fundo_id=${fundoId}&codigo_ativo=${encodeURIComponent(codigoAtivo)}${nomeParam}${admParam}`
    );
    return Array.isArray(response.data) ? response.data : (response.data as any).results ?? [];
  } catch (error) {
    handleError(error, 'ao buscar movimentações do ativo');
    throw error;
  }
};

export const createMovimentacaoAtivo = async (
  payload: MovimentacaoAtivoCreate
): Promise<MovimentacaoAtivo> => {
  try {
    const response = await api.post<MovimentacaoAtivo>('/ativos/movimentacao-ativo/', payload);
    return response.data;
  } catch (error) {
    handleError(error, 'ao criar movimentação');
    throw error;
  }
};

export const updateMovimentacaoAtivo = async (
  id: number,
  payload: Partial<MovimentacaoAtivoCreate>
): Promise<MovimentacaoAtivo> => {
  try {
    const response = await api.patch<MovimentacaoAtivo>(`/ativos/movimentacao-ativo/${id}/`, payload);
    return response.data;
  } catch (error) {
    handleError(error, 'ao editar movimentação');
    throw error;
  }
};

export const deleteMovimentacaoAtivo = async (id: number): Promise<void> => {
  try {
    await api.delete(`/ativos/movimentacao-ativo/${id}/`);
  } catch (error) {
    handleError(error, 'ao excluir movimentação');
    throw error;
  }
};

export const getDatasDisponiveis = async (fundoId: number): Promise<string[]> => {
  try {
    const response = await api.get<string[] | { results: string[] }>(
      `/ativos/carteira-diaria/fundo/${fundoId}/datas/`
    );
    return Array.isArray(response.data) ? response.data : response.data.results ?? [];
  } catch (error) {
    handleError(error, 'ao buscar datas disponíveis');
    throw error;
  }
};

export const getCarteiraDiariaFundo = async (
  fundoId: number,
  data?: string
): Promise<CarteiraDiariaFundoResponse> => {
  try {
    const url = data
      ? `/ativos/carteira-diaria/fundo/${fundoId}/data/${data}/`
      : `/ativos/carteira-diaria/fundo/${fundoId}/`;
    const response = await api.get<CarteiraDiariaFundoResponse>(url);
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar carteira diária do fundo');
    throw error;
  }
};

export const getRentabilidadeComparativa = async (
  fundoId: number
): Promise<RentabilidadeComparativaResponse> => {
  try {
    const response = await api.get<RentabilidadeComparativaResponse>(
      `/ativos/rentabilidade-comparativa/${fundoId}/`
    );
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar rentabilidade comparativa');
    throw error;
  }
};

export const exportHistoricoAtivo = async (
  fundoId: number,
  codigo1: string,
  adm: string,
  nome: string,
  classe?: string,
  subclasse?: string,
): Promise<void> => {
  try {
    const classeParam    = classe    ? `&classe=${encodeURIComponent(classe)}`       : '';
    const subclasseParam = subclasse ? `&subclasse=${encodeURIComponent(subclasse)}` : '';
    const response = await api.get(
      `/ativos/historico-ativo/exportar/?fundo_id=${fundoId}&codigo1=${encodeURIComponent(codigo1)}&adm=${encodeURIComponent(adm)}&nome=${encodeURIComponent(nome)}${classeParam}${subclasseParam}`,
      { responseType: 'blob' },
    );
    const url  = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const disposition: string = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename="(.+?)"/);
    link.href = url;
    link.setAttribute('download', match ? match[1] : 'historico_ativo.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error, 'ao exportar histórico do ativo');
    throw error;
  }
};

export const getHistoricoAtivo = async (
  fundoId: number,
  codigo1: string,
  adm: string,
  nome: string,
  page: number = 1,
  pageSize: number = 30,
  ordering: string = '-data_posicao',
): Promise<HistoricoAtivoResponse> => {
  try {
    const response = await api.get<HistoricoAtivoResponse>(
      `/ativos/historico-ativo/?fundo_id=${fundoId}&codigo1=${encodeURIComponent(codigo1)}&adm=${encodeURIComponent(adm)}&nome=${encodeURIComponent(nome)}&page=${page}&page_size=${pageSize}&ordering=${ordering}`
    );
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar histórico do ativo');
    throw error;
  }
};

export const exportHistoricoFundo = async (fundoId: number): Promise<void> => {
  try {
    const response = await api.get(
      `/ativos/historico-fundo/${fundoId}/exportar/`,
      { responseType: 'blob' },
    );
    const url  = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const disposition: string = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename="(.+?)"/);
    link.href = url;
    link.setAttribute('download', match ? match[1] : 'historico_fundo.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    handleError(error, 'ao exportar histórico do fundo');
    throw error;
  }
};

export const getHistoricoFundo = async (
  fundoId: number,
  page: number = 1,
  pageSize: number = 30,
  ordering: string = '-data_posicao',
): Promise<HistoricoFundoResponse> => {
  try {
    const response = await api.get<HistoricoFundoResponse>(
      `/ativos/historico-fundo/${fundoId}/?page=${page}&page_size=${pageSize}&ordering=${ordering}`
    );
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar histórico do fundo');
    throw error;
  }
};

export interface MovimentacaoCotistaFiltros {
  tipoOperacao?: 'APLICACAO' | 'RESGATE' | '';
  dataCampo?: 'solicitacao' | 'cotizacao' | 'impacto';
  dataInicio?: string;
  dataFim?: string;
}

const buildMovimentacaoCotistaParams = (fundoId: number, filtros: MovimentacaoCotistaFiltros = {}): URLSearchParams => {
  const params = new URLSearchParams({ fundo_id: String(fundoId) });
  if (filtros.tipoOperacao) params.set('tipo_operacao', filtros.tipoOperacao);
  if (filtros.dataCampo) params.set('data_campo', filtros.dataCampo);
  if (filtros.dataInicio) params.set('data_inicio', filtros.dataInicio);
  if (filtros.dataFim) params.set('data_fim', filtros.dataFim);
  return params;
};

export const getMovimentacaoCotista = async (
  fundoId: number,
  page: number = 1,
  pageSize: number = 30,
  ordering: string = '-data_solicitacao',
  filtros: MovimentacaoCotistaFiltros = {},
): Promise<MovimentacaoCotistaResponse> => {
  try {
    const params = buildMovimentacaoCotistaParams(fundoId, filtros);
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    params.set('ordering', ordering);

    const response = await api.get<MovimentacaoCotistaResponse>(
      `/ativos/movimentacao-cotista/?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar movimentações de cotistas');
    throw error;
  }
};

export interface MovimentacaoCotistaResumoItem {
  total: number;
  qtd: number;
}

export interface MovimentacaoCotistaResumo {
  aplicacao: MovimentacaoCotistaResumoItem;
  resgate: MovimentacaoCotistaResumoItem;
}

export const getMovimentacaoCotistaResumo = async (
  fundoId: number,
  filtros: MovimentacaoCotistaFiltros = {},
): Promise<MovimentacaoCotistaResumo> => {
  try {
    const params = buildMovimentacaoCotistaParams(fundoId, filtros);
    const response = await api.get<MovimentacaoCotistaResumo>(
      `/ativos/movimentacao-cotista/resumo/?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar resumo de movimentações de cotistas');
    throw error;
  }
};

export const getRentabilidadeAtivo = async (
  fundoId: number,
  codigo1: string,
  adm: string,
  nome: string,
): Promise<RentabilidadeAtivoResponse> => {
  try {
    const response = await api.get<RentabilidadeAtivoResponse>(
      `/ativos/rentabilidade-ativo/?fundo_id=${fundoId}&codigo1=${encodeURIComponent(codigo1)}&adm=${encodeURIComponent(adm)}&nome=${encodeURIComponent(nome)}`
    );
    return response.data;
  } catch (error) {
    handleError(error, 'ao buscar rentabilidade do ativo');
    throw error;
  }
};
